import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, Phone, User, Building, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { ContactForm as ContactFormType } from '../../types'

interface ContactFormProps {
  onSubmit?: (data: ContactFormType) => Promise<boolean>
  className?: string
  showTitle?: boolean
}

const ContactForm: React.FC<ContactFormProps> = ({ 
  onSubmit, 
  className = '',
  showTitle = true 
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<ContactFormType>({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errors, setErrors] = useState<Partial<ContactFormType>>({})

  // 表单验证
  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormType> = {}

    if (!formData.name.trim()) {
      newErrors.name = t('contact.form.validation.nameRequired')
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.validation.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.validation.emailInvalid')
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.validation.messageRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 处理输入变化
  const handleInputChange = (field: keyof ContactFormType, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  // 默认提交处理（EmailJS或其他服务）
  const defaultSubmitHandler = async (data: ContactFormType): Promise<boolean> => {
    // 这里可以集成EmailJS、Formspree或其他邮件服务
    try {
      console.log('Contact form submission:', data)
      
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // 这里应该是实际的邮件发送逻辑
      // 比如使用EmailJS:
      // await emailjs.send('service_id', 'template_id', data, 'public_key')
      
      return true
    } catch (error) {
      console.error('Form submission error:', error)
      return false
    }
  }

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const submitHandler = onSubmit || defaultSubmitHandler
      const success = await submitHandler(formData)

      if (success) {
        setSubmitStatus('success')
        // 重置表单
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: ''
        })
      } else {
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('Form submission failed:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`w-full max-w-2xl mx-auto ${className}`}>
      {showTitle && (
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('contact.description')}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 姓名 */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            <User size={16} className="inline mr-2" />
            {t('contact.form.name')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('contact.form.namePlaceholder')}
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* 公司/组织 */}
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
            <Building size={16} className="inline mr-2" />
            {t('contact.form.company')}
          </label>
          <input
            type="text"
            id="company"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            placeholder={t('contact.form.companyPlaceholder')}
            disabled={isSubmitting}
          />
        </div>

        {/* 邮箱 */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            <Mail size={16} className="inline mr-2" />
            {t('contact.form.email')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('contact.form.emailPlaceholder')}
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* 电话 */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            <Phone size={16} className="inline mr-2" />
            {t('contact.form.phone')}
          </label>
          <input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            placeholder={t('contact.form.phonePlaceholder')}
            disabled={isSubmitting}
          />
        </div>

        {/* 消息 */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            <MessageSquare size={16} className="inline mr-2" />
            {t('contact.form.message')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <textarea
            id="message"
            rows={5}
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-vertical ${
              errors.message ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('contact.form.messagePlaceholder')}
            disabled={isSubmitting}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600">{errors.message}</p>
          )}
        </div>

        {/* 提交按钮 */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-white font-medium transition-all duration-200 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 hover:scale-105 shadow-lg hover:shadow-xl'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('contact.form.sending')}
              </>
            ) : (
              <>
                <Send size={18} className="mr-2" />
                {t('contact.form.submit')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* 状态消息 */}
      <AnimatePresence>
        {submitStatus !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`mt-6 p-4 rounded-lg flex items-center ${
              submitStatus === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {submitStatus === 'success' ? (
              <CheckCircle size={20} className="mr-3 text-green-600" />
            ) : (
              <AlertCircle size={20} className="mr-3 text-red-600" />
            )}
            <span>
              {submitStatus === 'success'
                ? t('contact.form.success')
                : t('contact.form.error')
              }
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ContactForm
